var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import _ from 'lodash';
function isPercentage(p) {
    return typeof p === 'string' && p[p.length - 1] === '%';
}
function computePercentage(value, total) {
    if (value[value.length - 1] !== '%')
        throw new Error("Percentage has no percentage: \"" + value + "\"");
    var percentage = Number(value.slice(0, -1));
    return total * (percentage / 100);
}
function buildPadding(padding) {
    if (typeof padding === 'number') {
        var m = padding;
        return { top: m, right: m, bottom: m, left: m };
    }
    else if (Array.isArray(padding) && padding.length === 2) {
        var v = padding[0], h = padding[1];
        return { top: v, right: h, bottom: v, left: h };
    }
    else if (Array.isArray(padding)) {
        var _a = padding[0], top_1 = _a === void 0 ? 0 : _a, _b = padding[1], right = _b === void 0 ? 0 : _b, _c = padding[2], bottom = _c === void 0 ? 0 : _c, _d = padding[3], left = _d === void 0 ? 0 : _d;
        return { top: top_1, right: right, bottom: bottom, left: left };
    }
    else if (_.isObject(padding)) {
        return __assign({ top: 0, left: 0, right: 0, bottom: 0 }, padding);
    }
    else {
        throw new Error("Unrecognized margin format: " + JSON.stringify(padding));
    }
}
var defaultBlockProperties = {
    width: 'auto',
    height: 'auto',
    direction: 'row',
    padding: 0,
    top: 0,
    left: 0,
};
function makeBlocks(nodes, rootBlockNondefaulted) {
    var rootBlock = Object.assign({}, defaultBlockProperties, rootBlockNondefaulted);
    var ids = nodes.map(function (n) { return n.id; });
    var padding = buildPadding(rootBlock.padding);
    var availableWidth = rootBlock.width - padding.left - padding.right;
    var availableHeight = rootBlock.height - padding.top - padding.bottom;
    // make dimensions defaulted
    var defaultedWidths = nodes.map(function (n) { var _a; return (_a = n.width) !== null && _a !== void 0 ? _a : 'auto'; });
    var defaultedHeights = nodes.map(function (n) { var _a; return (_a = n.height) !== null && _a !== void 0 ? _a : 'auto'; });
    // compute percentages
    var widthsFlexible = defaultedWidths.map(function (w) {
        return isPercentage(w) ? computePercentage(w, availableWidth) : w;
    });
    var heightsFlexible = defaultedHeights.map(function (h) {
        return isPercentage(h) ? computePercentage(h, availableHeight) : h;
    });
    // compute 'auto'
    var widthsNonAuto = widthsFlexible.filter(function (w) { return w !== 'auto'; });
    var heightsNonAuto = heightsFlexible.filter(function (w) { return w !== 'auto'; });
    var takenWidth = _.sum(widthsNonAuto);
    var takenHeight = _.sum(heightsNonAuto);
    var countWidthsAuto = nodes.length - widthsNonAuto.length;
    var countHeightsAuto = nodes.length - heightsNonAuto.length;
    var widthAuto = rootBlock.direction === 'column'
        ? availableWidth
        : (availableWidth - takenWidth) / countWidthsAuto;
    var heightAuto = rootBlock.direction === 'row'
        ? availableHeight
        : (availableHeight - takenHeight) / countHeightsAuto;
    // replace 'auto'
    var widths = widthsFlexible.map(function (w) { return (w === 'auto' ? widthAuto : w); });
    var heights = heightsFlexible.map(function (h) { return (h === 'auto' ? heightAuto : h); });
    // stop abruptly if sizes overflow
    if (rootBlock.direction === 'row' && _.sum(widths) > availableWidth)
        throw new Error("Block widths are overflowing! " + widths.join('+') + " > " + availableWidth);
    if (rootBlock.direction === 'column' && _.sum(heights) > availableHeight)
        throw new Error("Block heights are overflowing! " + heights.join('+') + " > " + availableHeight);
    // position the elements
    var rootTop = rootBlock.top + padding.top;
    var rootLeft = rootBlock.left + padding.left;
    var lefts;
    var tops;
    if (rootBlock.direction === 'column') {
        lefts = widths.map(function () { return rootLeft; });
        tops = heights.map(function (_h, i) { return rootTop + _.sum(heights.slice(0, i)); });
    }
    else if (rootBlock.direction === 'row') {
        lefts = widths.map(function (_w, i) { return rootLeft + _.sum(widths.slice(0, i)); });
        tops = heights.map(function () { return rootTop; });
    }
    else {
        throw new Error("Unexpected LayoutNode direction: " + JSON.stringify(rootBlock.direction));
    }
    // create the blocks
    var blocks = _.times(nodes.length).map(function (i) {
        var id = ids[i];
        var node = nodes[i];
        var width = widths[i];
        var height = heights[i];
        var top = tops[i];
        var left = lefts[i];
        var bottom = top + height;
        var right = left + width;
        return { id: id, width: width, height: height, top: top, left: left, bottom: bottom, right: right, node: node };
    });
    // append their children blocks
    var childrenBlocks = blocks.flatMap(function (b) {
        return b.node.children !== undefined ? makeBlocks(b.node.children, __assign(__assign({}, b.node), b)) : [];
    });
    return __spreadArray(__spreadArray([], blocks), childrenBlocks);
}
export function makeLayout(root) {
    var _a, _b;
    var rootBlock = __assign({ left: 0, top: 0, right: root.width + ((_a = root.left) !== null && _a !== void 0 ? _a : 0), bottom: root.height + ((_b = root.top) !== null && _b !== void 0 ? _b : 0) }, _.omit(root, 'children', 'node', 'padding', 'direction'));
    var blocks = root.children ? __spreadArray([rootBlock], makeBlocks(root.children, root)) : [rootBlock];
    return _.keyBy(blocks, 'id');
}
//# sourceMappingURL=index.js.map